// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, FormEvent, useEffect } from 'react'
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { submitFeedback, FeedbackInput, ApiError } from '../api/client'

const MAX_CHARS = 1000

interface Props {
  screen: string
  onClose: () => void
}

export default function FeedbackWidget({ screen, onClose }: Props) {
  const [category, setCategory] = useState<FeedbackInput['category']>('bug')
  const [severity, setSeverity] = useState<FeedbackInput['severity']>('medium')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)
  const [phiConfirmNeeded, setPhiConfirmNeeded] = useState(false)

  // Close on escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const charsRemaining = MAX_CHARS - message.length
  const tooLong = charsRemaining < 0
  const isPatientCare = category === 'patient_care'

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (tooLong || message.trim().length === 0) return
    setSubmitting(true)
    setResult(null)
    try {
      const res = await submitFeedback({
        screen,
        category,
        severity,
        message: message.trim(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        phi_acknowledged: phiConfirmNeeded,
      })
      setResult({ ok: true, text: res.note })
      setMessage('')
      setPhiConfirmNeeded(false)
    } catch (err) {
      const apiErr = err as ApiError
      // Possible-PHI soft-block: first time, ask the user to confirm there's no personal data,
      // then a second submit resends with phi_acknowledged=true.
      if (apiErr.status === 422 && apiErr.body?.error === 'possible_phi_detected') {
        setPhiConfirmNeeded(true)
        setResult({ ok: false, text: "That looks like it may contain an SSN or personal data. This form isn't for patient information — please remove it. If you're sure there's none, submit again to confirm." })
      } else {
        setResult({
          ok: false,
          text: apiErr.status === 429
            ? "You're sending feedback too fast — please wait a moment."
            : `Couldn't submit: ${apiErr.message}`,
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      style={overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={modalStyle}>
        <header style={modalHeaderStyle}>
          <h2 id="feedback-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            Send feedback
          </h2>
          <button onClick={onClose} aria-label="Close feedback form" style={{ padding: 6 }}>
            <X size={20} />
          </button>
        </header>

        {/* CO-1 mandate: PHI warning above the textarea */}
        <div role="note" style={phiWarningStyle}>
          <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>Do not include patient information.</strong>{' '}
            No names, dates of birth, prescription numbers, or anything that
            could identify a specific person. Feedback is reviewed by the team
            and may be visible to other users on the public roadmap.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldGroupStyle}>
            <label htmlFor="fb-category" style={labelStyle}>What kind of feedback?</label>
            <select
              id="fb-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackInput['category'])}
              style={inputStyle}
            >
              <option value="bug">Something is broken</option>
              <option value="feature_request">I'd like a new feature</option>
              <option value="ux">The interface is confusing</option>
              <option value="data_correction">Data looks wrong</option>
              <option value="patient_care">Report a patient-care issue</option>
              <option value="other">Other</option>
            </select>
          </div>

          {isPatientCare && (
            <div role="note" style={{ ...phiWarningStyle, background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}>
              <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                Patient-care reports go to the compliance team and are <em>not</em> visible
                on the public roadmap. Still avoid including patient identifiers — describe
                the issue generically (e.g., "a patient's allergy alert didn't fire").
              </div>
            </div>
          )}

          <div style={fieldGroupStyle}>
            <label htmlFor="fb-severity" style={labelStyle}>How urgent?</label>
            <select
              id="fb-severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as FeedbackInput['severity'])}
              style={inputStyle}
            >
              <option value="low">Low — nice to have</option>
              <option value="medium">Medium — slowing me down</option>
              <option value="high">High — blocking my work</option>
            </select>
          </div>

          <div style={fieldGroupStyle}>
            <label htmlFor="fb-message" style={labelStyle}>What's going on?</label>
            <textarea
              id="fb-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              style={{ ...inputStyle, resize: 'vertical' }}
              maxLength={MAX_CHARS + 100}
              placeholder="Describe the issue or idea. The more specific, the more useful."
              required
            />
            <div style={{
              fontSize: 12,
              color: tooLong ? 'var(--color-danger)' : 'var(--color-text-mute)',
              marginTop: 4,
            }}>
              {tooLong
                ? `Too long by ${-charsRemaining} characters (max ${MAX_CHARS})`
                : `${charsRemaining} characters remaining`}
            </div>
          </div>

          {result && (
            <div role="status" style={{
              ...phiWarningStyle,
              background: result.ok ? '#d1fae5' : '#fee2e2',
              borderColor: result.ok ? '#6ee7b7' : '#fca5a5',
              color: result.ok ? 'var(--color-success)' : 'var(--color-danger)',
            }}>
              {result.ok ? <CheckCircle2 size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
              <div>{result.text}</div>
            </div>
          )}

          <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>Cancel</button>
            <button
              type="submit"
              disabled={submitting || tooLong || message.trim().length === 0}
              style={{
                ...primaryButtonStyle,
                opacity: (submitting || tooLong || message.trim().length === 0) ? 0.5 : 1,
                cursor: (submitting || tooLong || message.trim().length === 0) ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Sending...' : 'Send feedback'}
            </button>
          </footer>
        </form>

        <p style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '12px 0 0', textAlign: 'center' }}>
          Screen: <code>{screen}</code>
        </p>
      </div>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(28,25,23,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16,
}

const modalStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 24,
  maxWidth: 560,
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: 'var(--shadow-lg)',
}

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
}

const phiWarningStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  padding: 12,
  borderRadius: 'var(--radius-md)',
  fontSize: 13,
  marginBottom: 16,
  alignItems: 'flex-start',
}

const fieldGroupStyle: React.CSSProperties = { marginBottom: 14 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: 'var(--color-text-soft)' }
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
}
const primaryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: 'var(--color-primary)',
  color: 'white',
  borderRadius: 'var(--radius-md)',
  fontWeight: 500,
  fontSize: 14,
}
const secondaryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: 'transparent',
  color: 'var(--color-text-soft)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 500,
  fontSize: 14,
}
